import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import {
  AlertComponent,
  CheckboxComponent,
  EnumHelper,
  FormGroupComponent,
  InputErrorsComponent,
  InputLabelComponent,
  MbsPopupType,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  ModalSettings,
  NumberComponent,
  SelectComponent
} from 'mbs-ui-kit';
import { MonthNamesEnum } from 'mbs-ui-shared@models/MonthNames.enum';
import { Period, PeriodDisplay } from 'mbs-ui-shared@models/Period.enum';
import { ImmutabilityConfirmModalComponent } from 'mbs-ui-shared@modules/immutability-modals/immutability-confirm-modal/immutability-confirm-modal.component';
import { noop } from 'rxjs';
import {
  getGFSPolicyFormGroup,
  monthlyMoreYValidator,
  moreThatRetention,
  weeklyMoreMYValidator
} from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.utils';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { RouterLink } from '@angular/router';

const si18nKey = 'wizards:common.s';
interface PeriodForCheck {
  count: number;
  period: Period;
}

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-gfs-modal-component',
  templateUrl: './GFS-modal.component.html',
  imports: [
    AlertComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputErrorsComponent,
    InputLabelComponent,
    ModalComponent,
    NgbTooltipModule,
    NumberComponent,
    ReactiveFormsModule,
    RouterLink,
    SelectComponent,
    ModalFooterDirective
  ]
})
export class NBFGFSModalComponent implements OnInit {
  ability = inject(AbilityService);
  private modalService = inject(ModalService);
  private i18nextService = inject(I18NextService);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public readonly mbsPopupType = MbsPopupType;
  public readonly labels = {
    weeks: `${this.i18nextService.t('wizards:common.week', { format: 'title' })}(${this.i18nextService.t(si18nKey)})`,
    months: `${this.i18nextService.t('wizards:common.month', { format: 'title' })}(${this.i18nextService.t(si18nKey)})`,
    years: `${this.i18nextService.t('wizards:common.year', { format: 'title' })}(${this.i18nextService.t(si18nKey)})`
  };
  public readonly elementSelectors = {
    id: {
      weeklyPeriodInput: 'NBF-GFS-Modal-weekly-period-input',
      monthlyPeriodInput: 'NBF-GFS-Modal-monthly-period-input',
      yearlyPeriodInput: 'NBF-GFS-Modal-yearly-period-input',
      yearlyFirstMonth: 'NBF-GFS-Modal-yearly-first-month',
      retentionPeriodAlert: 'NBF-GFS-Modal-retention-period-alert',
      objectLockAlert: 'NBF-GFS-Modal-object-lock-alert',
      saveButton: 'NBF-GFS-Modal-save-button',
      cancelButton: 'NBF-GFS-Modal-cancel-button'
    }
  };

  public form: UntypedFormGroup = new UntypedFormGroup({});
  public currentRetentionInfo = '';
  public monthNames = EnumHelper.EnumToSelectIndexesArray(MonthNamesEnum);
  public canShowImmutabilityByStorage: boolean;
  public retentionLessThatStorageObjectLock = false;
  public retentionPeriodAlert: { weekly: boolean; monthly: boolean; yearly: boolean } = {
    weekly: false,
    monthly: false,
    yearly: false
  };

  private retention: PeriodForCheck = null;
  private init = false;

  get GFSPolicySettingsCtrl(): FormGroup<{ weekly: FormGroup; monthly: FormGroup }> {
    return <FormGroup<{ weekly: FormGroup; monthly: FormGroup }>>this.form;
  }

  get weeksErrors(): string {
    const GFSPolicySettingsCtrl = this.GFSPolicySettingsCtrl;

    if (GFSPolicySettingsCtrl?.controls?.weekly?.controls?.storePeriod?.errors?.moreThanMonthly) {
      return this.i18nextService.t('wizards:retention.GFSModal.longerThatMonthly');
    }

    if (GFSPolicySettingsCtrl?.controls?.weekly?.controls?.storePeriod?.errors?.moreThanYearly) {
      return this.i18nextService.t('wizards:retention.GFSModal.longerThatYearly');
    }

    return '';
  }

  get monthsErrors(): string {
    const GFSPolicySettingsCtrl = this.GFSPolicySettingsCtrl;

    if (GFSPolicySettingsCtrl?.controls?.monthly?.controls?.storePeriod?.errors?.moreThanYearly) {
      return this.i18nextService.t('wizards:retention.GFSModal.longerThatYearly');
    }

    return '';
  }

  ngOnInit() {
    this.initForm();
    this.fullData();

    this.init = true;
  }

  monthlyStorePeriodChangeHandler(): void {
    const weekly = this.form.get('weekly');

    if (weekly?.value?.enabled) {
      weekly.get('storePeriod').updateValueAndValidity();
    }
  }

  yearlyStorePeriodChangeHandler(): void {
    const weekly = this.form.get('weekly');
    const monthly = this.form.get('monthly');

    if (weekly?.value?.enabled) {
      weekly.get('storePeriod').updateValueAndValidity();
    }
    if (monthly?.value?.enabled) {
      monthly.get('storePeriod').updateValueAndValidity();
    }
  }

  fullBackupsChangeHandler(event: boolean, name: 'weekly' | 'monthly' | 'yearly'): void {
    const action = event ? 'enable' : 'disable';

    if (name === 'yearly') {
      (this.form.get(name) as UntypedFormGroup).controls.storePeriod[action]();
      (this.form.get(name) as UntypedFormGroup).controls.takeBackupFromMonth[action]();
      this.yearlyStorePeriodChangeHandler();
    } else {
      (this.form.get(name) as UntypedFormGroup).controls.storePeriod[action]();
      if (name === 'monthly') this.monthlyStorePeriodChangeHandler();
    }

    this.checkGFS();

    if (!event) {
      delete this.retentionPeriodAlert[name];
    }
  }

  enableImmutabilityChangeHandler(event: boolean): void {
    if (event) {
      this.modalService
        .openCustom(ImmutabilityConfirmModalComponent, { ...new ModalSettings(), size: MbsSize.md })
        .then(noop)
        .catch((result) => !result && this.form.get('enabledImmutability').reset(false));
    }
  }

  saveClickHandler(): void {
    if (!this.form.valid) {
      return;
    }

    const hasGFS = ['weekly', 'monthly', 'yearly'].some((key) => !!this.form.get(key).get('enabled').value);

    if (!hasGFS) {
      this.form.get('enabledImmutability').setValue(false);
    }

    this.baseModal.save({ data: this.form.getRawValue() });
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }

  private weeklyMoreMYValidator(control: AbstractControl): ValidationErrors | null {
    return this.init ? weeklyMoreMYValidator(this.form, control) : null;
  }

  private monthlyMoreYValidator(control: AbstractControl): ValidationErrors | null {
    return this.init ? monthlyMoreYValidator(this.form, control) : null;
  }

  private initForm() {
    this.form = getGFSPolicyFormGroup(this.weeklyMoreMYValidator.bind(this), this.monthlyMoreYValidator.bind(this));

    ['weekly', 'monthly', 'yearly'].forEach((key) => {
      this.form.get(key).get('storePeriod').addValidators(this.retentionValidator.bind(this, key));
    });
  }

  private fullData(): void {
    const data = this.baseModal?.data;
    const GFS = data?.GFS;
    const retention: PeriodForCheck = data?.retention;
    const storageObjectLock: PeriodForCheck = data?.storageObjectLock;
    const periodEnumSelectArray = EnumHelper.EnumToSelectIndexesArray(Period, PeriodDisplay);
    const periodTitle = String(periodEnumSelectArray.find((item) => item.value === retention.period).label);

    this.retentionLessThatStorageObjectLock = storageObjectLock ? moreThatRetention(storageObjectLock, retention) : false;
    this.retention = this.retentionLessThatStorageObjectLock ? storageObjectLock : retention;
    this.form.reset(GFS);
    this.currentRetentionInfo = `${retention.count} ${(periodTitle || '').toLowerCase()}`;
    this.canShowImmutabilityByStorage = data.canShowImmutabilityByStorage;
    if (!this.canShowImmutabilityByStorage) {
      this.form.get('enabledImmutability').setValue(false);
      this.form.get('enabledImmutability').disable();
    }

    ['weekly', 'monthly', 'yearly'].forEach((key) => {
      (this.form.get(key) as UntypedFormGroup).controls.storePeriod[this.form.get(key).get('enabled').value ? 'enable' : 'disable']();
      (this.form.get(key) as UntypedFormGroup).controls.takeBackupFromMonth?.[
        this.form.get(key).get('enabled').value ? 'enable' : 'disable'
      ]();
    });

    const scheduleMonthly = data.schedule?.fullSchedule?.recurringPeriod === 'Monthly';
    const scheduleCount = data.schedule?.fullSchedule?.repeatEveryCount || 0;

    if (scheduleMonthly ? scheduleCount < 12 : scheduleCount >= 30) {
      this.offWeekly();
    }

    if (scheduleMonthly && scheduleCount >= 12) {
      this.offWeekly();
      this.offMonthly();
    }

    this.checkGFS();
  }

  private retentionValidator(type: 'weekly' | 'monthly' | 'yearly', control: AbstractControl): ValidationErrors | null {
    if (control.disabled || !this.form.get(type).get('enabled').value) {
      return void delete this.retentionPeriodAlert[type];
    }

    let period: Period;

    switch (type) {
      case 'weekly':
        period = Period.week;
        break;
      case 'monthly':
        period = Period.month;
        break;
      case 'yearly':
        period = Period.year;
        break;
      default:
        break;
    }

    const isOk = moreThatRetention({ count: control.value, period }, { count: this.retention.count, period: this.retention.period });

    if (!isOk) {
      this.retentionPeriodAlert[type] = true;

      return { minThatRetention: true };
    }

    return void delete this.retentionPeriodAlert[type];
  }

  private offWeekly(): void {
    this.form.get('weekly').get('enabled').setValue(false);
    this.form.get('weekly').get('enabled').disable();
    this.form.get('weekly').get('storePeriod').disable();
  }

  private offMonthly(): void {
    this.form.get('monthly').get('enabled').setValue(false);
    this.form.get('monthly').get('enabled').disable();
    this.form.get('monthly').get('storePeriod').disable();
  }

  private checkGFS(): void {
    if (!this.canShowImmutabilityByStorage) {
      return;
    }

    const hasGFS = ['weekly', 'monthly', 'yearly'].some((key) => !!this.form.get(key).get('enabled').value);

    if (!hasGFS) {
      this.form.get('enabledImmutability').disable();
      this.form.get('enabledImmutability').setValue(false);

      return;
    }

    this.form.get('enabledImmutability').enable();
  }
}
